import { getImageTextureObj } from 'support/generalUtils';
import { translate } from 'support/translate';
import { RoutePath } from 'config/routes';
import AbstractDetailsPage, {
  AbstractDetailsPageTemplateSpec,
} from './AbstractDetailsPage';
import {
  SelectableTile,
  SelectableTileType,
} from 'components/common/tiles/SelectableTile';
import PortraitTile from 'components/common/tiles/PortraitTile';
import { AppData, Lightning } from '@lightningjs/sdk';
import { Show, Video } from 'types/api/media';
import { PageId } from 'types/pageId';
import { getWatchlistLaneItems } from 'support/personaUtils';
import { MultipleSeriesNameTypes } from 'services/analytics/MParticle';
import { reportMyStuffAddRemove } from 'services/analytics/reportingServicePage';

const BOOKMARK_ICON_WIDTH = 48;
const BOOKMARK_ICON_HEIGHT = 64;

const BOOKMARK_ICON = getImageTextureObj(
  'static/images/bookmark-icon.svg',
  BOOKMARK_ICON_WIDTH,
  BOOKMARK_ICON_HEIGHT,
);

const GRID_SPACING = 20;
const GRID_COLUMNS = 6;

export interface MyWatchlistPageTemplateSpec
  extends AbstractDetailsPageTemplateSpec {
  pageData: {
    watchlistLaneItems: (Show | Video)[];
  };
}

export default class MyWatchlistPage
  extends AbstractDetailsPage<MyWatchlistPageTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<MyWatchlistPageTemplateSpec>
{
  protected override _pageId = PageId.WATCHLIST;
  override get announce() {
    return translate('myStuff.watchlist');
  }

  override $remove() {
    super.$remove();
    const numTilesSelected = this._ItemsList.items.filter(
      item => !!(item as SelectableTileType).isSelected,
    ).length;

    this.widgets.removeconfirmationmodal.update({
      numberOfItems: numTilesSelected,
      detailsTitle: translate('myStuff.watchlist'),
    });
    AppData?.modalService.open('RemoveConfirmationModal');
  }

  override async $removeItems() {
    super.$removeItems();

    const idsToRemove: string[] = [];
    this._ItemsList.items.forEach(item => {
      const tile = item as SelectableTileType;
      if (tile.isSelected) {
        idsToRemove.push(tile.data?.id ?? '');
      }
    });
    if (idsToRemove.length > 0) {
      await AppData!.personaService?.removeFromWatchlist(idsToRemove);
      await this.reportRemoveWatchlistItems(idsToRemove);
      await this.fetchItems();
      this.setupWatchlistGrid();

      // Toggle back edit mode
      this.$toggleEdit();
    }
  }

  override async _onDataProvided() {
    this.loginConfig = AppData?.config?.login.logged_in;
    this.items = this.pageData?.watchlistLaneItems ?? [];
    this.setupWatchlistGrid();
    this.setupRemoveConfirmationModal();
  }

  private async fetchItems() {
    const watchlistLaneItems = await getWatchlistLaneItems();
    this.items = watchlistLaneItems ?? [];
  }

  private setupWatchlistGrid() {
    this.loaded = false;

    const watchlistText = this.loginConfig?.watchlist;

    // Assume user is logged in
    if (this.items.length > 0) {
      const listItems = this.items as (Show | Video)[];
      const gridItems = listItems.map(item => ({
        type: SelectableTile(PortraitTile),
        data: item,
        imageUrl: item.images?.image_vertical || '',
      }));
      this._ItemsList.patch({
        columns: GRID_COLUMNS,
        spacing: GRID_SPACING,
        crossSpacing: GRID_SPACING,
        listType: SelectableTile(PortraitTile),
        items: gridItems,
      });

      this.showEmptyGridSection = false;
      this.showEditButton = true;
      this.title = translate('myStuff.watchlist');
      this.subtitle = '';
    } else if (watchlistText) {
      this.showEditButton = false;
      this._EmptySection.patch({
        icon: BOOKMARK_ICON,
        title: watchlistText?.title,
        description: watchlistText?.description,
        buttonLabel: watchlistText?.cta_button_text,
        action: RoutePath.HOME_SCREEN,
      });
      this.showEmptyGridSection = true;
      this._ItemsList.items = [];
    }
    this.loaded = true;
  }

  private reportRemoveWatchlistItems(watchlist: string[]) {
    if (!watchlist || watchlist.length <= 0) return;

    // type can be the {seriesName} (single item), Multiple(1+ items) or Select All(all items),
    let type: MultipleSeriesNameTypes | string;

    if (watchlist.length === this._ItemsList.items.length) {
      type = 'Select All';
    } else if (watchlist.length === 1) {
      const channel = this._ItemsList.items.find(
        item => (item.data as Video | Show).id === watchlist[0],
      );
      const channelData = channel?.data as Video | Show;
      type = channelData.title ?? '';
    } else {
      type = 'Multiple';
    }

    reportMyStuffAddRemove({
      actionType: 'Remove',
      type,
      pageId: this.pageId,
      rowName: 'Watchlist',
    });
  }
}
